"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="207121" className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  const desiredOrder = ["Home", "How It Works", "Pickup Areas", "FAQ", "Schedule Pickup"];
  const displayItems = menuItems.map(item => ({
    ...item,
    label: item.label.trim()
  })).filter(item => desiredOrder.includes(item.label)).sort((a, b) => desiredOrder.indexOf(a.label) - desiredOrder.indexOf(b.label));
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {displayItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={`font-default tracking-normal text-sm transition-all focus:outline-none ${item.label === "Schedule Pickup" ? "text-primary font-bold px-5 py-2.5 bg-primary/10 rounded-full hover:bg-primary/15 shadow-sm border border-primary/10" : "text-foreground hover:text-primary"}`}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-12">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-8 w-32 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  const desiredOrder = ["Home", "How It Works", "Pickup Areas", "FAQ", "Schedule Pickup"];
  const displayItems = menuItems.map(item => ({
    ...item,
    label: item.label.trim()
  })).filter(item => desiredOrder.includes(item.label)).sort((a, b) => desiredOrder.indexOf(a.label) - desiredOrder.indexOf(b.label));
  return <div className="flex flex-col gap-6 mt-12">
      {displayItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="text-foreground font-default tracking-normal text-xl hover:text-primary transition-colors">
          {item.label}
        </Link>)}
      <div className="mt-8 pt-8 border-t border-border">
        <Button variant="secondary" className="w-full h-12 text-base rounded-xl tracking-normal font-default shadow-sm active:scale-95 transition-all">
          Schedule Free Pickup
        </Button>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-md" : "bg-background border-b border-transparent"}`}>
      <div className="container mx-auto px-4 md:px-6 h-24 flex items-center justify-between gap-x-8">
        {}
        <Link to="/" className="flex-shrink-0 z-10">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1623234/img/outbacktours_logo.png" alt="Second Chance Threads Logo" className="h-7 w-auto object-contain" />
        </Link>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="18">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden md:flex flex-shrink-0 z-10 ml-4">
          <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl tracking-normal font-default shadow-md hover:shadow-lg transition-all">
            Schedule Free Pickup
          </Button>
        </div>

        {}
        <div className="md:hidden flex items-center z-10">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open Menu" className="text-foreground">
                <Menu className="size-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-background border-l border-border">
              <MenuProvider menu_id="18">
                <MobileMenuConsumer />
              </MenuProvider>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {}
      <div className="fixed inset-x-0 bottom-0 md:hidden z-50 bg-background/90 backdrop-blur-md p-4 border-t border-border">
        <Button className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl tracking-normal font-default shadow-lg active:scale-95 transition-all">
          Schedule Free Pickup
        </Button>
      </div>
    </section>;
}